import React, { useContext, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { WalletContext } from '../context/WalletContex';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import apiClient from '../utils/apiClient';
import { HiCheckCircle, HiExclamationTriangle } from 'react-icons/hi2';

export default function EmailVerified() {
  const { wallet, setWallet } = useContext(WalletContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const failed = searchParams.get('status') === 'expired' || searchParams.get('status') === 'error';

  const isPWA = window.navigator.standalone === true
    || window.matchMedia('(display-mode: standalone)').matches;

  const handleContinue = () => {
    /* Works for Safari users — PWA users get synced via pageshow/visibilitychange */
    if (wallet) {
      const updated = { ...wallet, emailVerified: true };
      localStorage.setItem('ikhuaWallet', JSON.stringify(updated));
      setWallet(updated);
    }
    navigate(wallet ? '/home' : '/welcome');
  };

  if (failed) {
    return <ExpiredLink />;
  }

  return (
    <div style={{
      minHeight: '100dvh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', textAlign: 'center',
      padding: '40px 28px', background: '#090817', gap: 24,
      maxWidth: 430, margin: '0 auto',
    }}>
      <div style={{ width: 80, height: 80, borderRadius: 28, background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <HiCheckCircle size={40} style={{ color: '#10B981' }} />
      </div>

      <div>
        <h1 style={{ fontSize: '26px', fontWeight: 800, color: '#fff', margin: '0 0 12px' }}>
          Email Verified!
        </h1>
        <p style={{ color: '#8B8FA8', fontSize: '14px', lineHeight: 1.7, margin: 0 }}>
          Your email address has been verified. Your wallet backup is now fully secured.
        </p>
      </div>

      {isPWA ? (
        /* This page opened in Safari from the email link — PWA is a separate app */
        <div style={{ background: '#110E30', border: '1px solid rgba(124,92,246,0.25)', borderRadius: 20, padding: '20px 22px' }}>
          <p style={{ color: '#C4C9E2', fontSize: '14px', fontWeight: 600, margin: '0 0 8px' }}>
            Return to the Ikhua app on your home screen
          </p>
          <p style={{ color: '#4B5170', fontSize: '13px', lineHeight: 1.6, margin: 0 }}>
            Your verified status will update automatically when you switch back. You can close this tab.
          </p>
        </div>
      ) : (
        /* Regular Safari / browser user — localStorage is shared with the app */
        <Button variant="primary" size="lg" full onClick={handleContinue}>
          Go to Wallet
        </Button>
      )}
    </div>
  );
}

function ExpiredLink() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const resend = async () => {
    if (!email.trim()) { setError('Enter your email address'); return; }
    setError('');
    setSending(true);
    try {
      await apiClient.post('/wallet/resend-verification', { email: email.trim() });
      // Always shows success — backend never reveals whether the account exists.
      setSent(true);
    } catch {
      // Network/server failure only — still don't say anything account-specific.
      setSent(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{
      minHeight: '100dvh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', textAlign: 'center',
      padding: '40px 28px', background: '#090817', gap: 24,
      maxWidth: 430, margin: '0 auto',
    }}>
      <div style={{ width: 80, height: 80, borderRadius: 28, background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <HiExclamationTriangle size={40} style={{ color: '#F59E0B' }} />
      </div>

      <div>
        <h1 style={{ fontSize: '24px', fontWeight: 800, color: '#fff', margin: '0 0 12px' }}>
          Link Expired
        </h1>
        <p style={{ color: '#8B8FA8', fontSize: '14px', lineHeight: 1.7, margin: 0 }}>
          This verification link is invalid or has expired. Enter your email below and we'll send a new one if needed.
        </p>
      </div>

      {sent ? (
        <div style={{ background: '#110E30', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 20, padding: '20px 22px' }}>
          <p style={{ color: '#C4C9E2', fontSize: '14px', fontWeight: 600, margin: 0 }}>
            If an account exists with that email, a new verification link is on its way. Check your inbox.
          </p>
        </div>
      ) : (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Input
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            autoCapitalize="none"
            error={error}
          />
          <Button variant="primary" size="lg" full onClick={resend} disabled={sending}>
            {sending ? 'Sending…' : 'Resend verification link'}
          </Button>
        </div>
      )}
    </div>
  );
}
